import { CSSResultGroup, html, PropertyValues } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { style } from './rating.style.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { ValueFormElement } from 'elements/_shared/formElement.js'

export const iconSizes = ['xxs', 'xs', 'sm', 'md', 'lg', 'xl'] as const
export type IconSize = typeof iconSizes[number]

initializeIconsByRole({
  'cse-rating.empty': { name: 'star', style: 'light' },
  'cse-rating.half-filled': { name: 'star-half-stroke', style: 'solid' },
  'cse-rating.filled': { name: 'star', style: 'solid' },
})

@customElement('cse-rating')
export class CSERating extends ValueFormElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ type: String, reflect: true })
  value = '0'

  @property({ type: Boolean })
  editable = false

  @property({ type: String })
  name = ''

  @property({ type: Boolean })
  disabled = false

  @property({ reflect: true })
  size: IconSize = 'md'

  @state()
  internalValue = 0

  override updated(changedProperties: PropertyValues) {
    if (changedProperties.has('value')) {
      let newValue: number = parseFloat(this.value || '0')
      newValue = Math.round(newValue * 2) / 2

      this.internalValue = Math.max(0, Math.min(newValue, 5))
    }
  }

  handleClick = (rating: number) => {
    if (this.editable && !this.disabled) {
      this.value = rating.toString()
    }
  }

  mapIconRole(index: number) {
    if (
      this.internalValue > index
      && this.internalValue < index + 1
    ) {
      return 'cse-rating.half-filled'
    } else if (index < this.internalValue) {
      return 'cse-rating.filled'
    } else {
      return 'cse-rating.empty'
    }
  }

  override render() {
    return html`
      <div class="cse-rating" data-value="${this.internalValue}">
        ${[...Array(5)].map((_, index) => html`
          <cse-icon
            class="${classMap({
              'disabled': this.disabled
            })}"
            icon-role="${this.mapIconRole(index)}"
            size="${this.size}"
            @click="${() => this.handleClick(index + 1)}"
          ></cse-icon>
        `)}
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cse-rating': CSERating
  }
}
